import { DOCUMENT } from '@angular/common';
import { Component, Inject, Injector, isDevMode, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { CbreLogger, CbreLoggerToken, LoggerConfiguratorService, LogMessage } from '@cbre/logger';
import { environment } from '@env/environment';
import '@shared/behaviors/array-behavior-extension';
import '@shared/functions/string-boost';
import { DecoratorServiceProvider } from '@core/utils/decorator-service-provider';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { CbreIconLoaderService } from '@shared/services/cbre-icon-loader.service';
import { TitleService } from '@shared/services/title.service';
import { L10nTranslationService } from 'angular-l10n';
import { filter, take } from 'rxjs/operators';

declare const gtag: any;

@Component({
  selector: 'miq-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit {
  isProd = environment.production;
  /** Hidden until the first navigation ends, and on routes flagged with `data.hideMaintenanceBanner` (e.g. /pdf). */
  showMaintenanceBanner = false;
  private userId: number;

  constructor(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cbreIconLoaderService: CbreIconLoaderService, // resources loader service
    titleService: TitleService,
    private router: Router,
    private userInfoResolver: UserInfoResolver,
    private translationService: L10nTranslationService,
    @Inject(CbreLoggerToken) private logger: CbreLogger,
    private jsLoggerConfigurator: LoggerConfiguratorService<LogMessage>,
    @Inject(DOCUMENT) private document: Document,
    injector: Injector
  ) {
    DecoratorServiceProvider.setInjector(injector);
    titleService.setTitleAsBaseName();
    // configure vanilla logger
    this.jsLoggerConfigurator.init();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.showMaintenanceBanner = !this.isBannerHiddenByRoute();
    });
    if (!isDevMode()) {
      this.initializeGtag();
      this.subscribeToUserNavigation();
    }
  }

  ngOnInit(): void {
    this.removeAppShellSkeleton();

    this.translationService
      .onError()
      .pipe(filter(Boolean))
      .subscribe(error => this.logger.error(error));

    this.logger.debug(`MarketIQ Debug mode is enabled at ${new Date()}`);
  }

  /**
   * Removes the static skeleton in index.html once the router has settled.
   *
   * It is a sibling of <miq-root> rather than a child precisely so bootstrap does not
   * take it away: bootstrap completes long before the first route renders, and
   * app.component.html is a bare <router-outlet>, so removing it at bootstrap left a
   * white page for the whole guard/resolver/lazy-chunk chain.
   *
   * Cancel and Error are terminal too — without them a blocked navigation would leave
   * the skeleton covering the app forever.
   */
  private removeAppShellSkeleton() {
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError
        ),
        take(1)
      )
      .subscribe(() => this.document.getElementById('miq-skeleton')?.remove());
  }

  private isBannerHiddenByRoute(): boolean {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.pathFromRoot.some(r => r.data?.hideMaintenanceBanner === true);
  }

  private initializeGtag() {
    const userInfo = this.userInfoResolver.resolve();
    if (!this.userId && userInfo != null && userInfo.id != null) {
      this.userId = userInfo.id;
    }
    if (this.userId) {
      gtag('set', { user_id: this.userId });
    }
  }

  private subscribeToUserNavigation() {
    this.router.events.subscribe(event => {
      if (gtag != null && event instanceof NavigationEnd) {
        gtag('config', 'GTM-MHLQ7NMV', {
          page_path: event.urlAfterRedirects,
        });
      }
    });
  }
}
